import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { cn } from '@goaltree/ui/lib/utils'
import { useMutation, useQuery } from 'convex/react'
import type { FunctionReturnType } from 'convex/server'
import {
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
  Trash2,
  Volume2,
  VolumeX,
  X,
} from 'lucide-react'
import { type CSSProperties, useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { habitColor } from '@/components/inbox-habits'
import { UserAvatar } from '@/components/user-avatar'

export type StoryPerson = FunctionReturnType<typeof api.stories.rail>[number]
type StoryItem = StoryPerson['items'][number]

const PHOTO_MS = 5000
const VIDEO_MAX_MS = 15_000
const HOLD_MS = 220

function storyAgo(ms: number, locale: string) {
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'narrow' })
  const mins = Math.max(1, Math.round((Date.now() - ms) / 60_000))
  if (mins < 60) return rtf.format(-mins, 'minute')
  return rtf.format(-Math.round(mins / 60), 'hour')
}

/** Shares today's check-in capture to your story, with its own toasts. */
export function useAddToStory() {
  const { t } = useTranslation()
  const add = useMutation(api.stories.add)
  return useCallback(
    (habitId: Id<'nodes'>, date: string) =>
      add({ habitId, date })
        .then(() => toast.success(t('stories.added')))
        .catch(() => toast.error(t('stories.addFailed'))),
    [add, t],
  )
}

export function StoryRing({
  person,
  size,
  onOpen,
}: {
  person: Pick<StoryPerson, 'name' | 'image' | 'allSeen' | 'isMe'>
  size: number
  onOpen: () => void
}) {
  const { t } = useTranslation()
  const label = person.isMe ? t('stories.yourStory') : person.name
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={t('stories.open', { name: label })}
      className="group flex w-[72px] shrink-0 cursor-pointer flex-col items-center gap-1.5 rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-ring/60"
    >
      <span
        className={cn(
          'grid place-items-center rounded-full p-[2.5px] transition-transform group-active:scale-95',
          person.allSeen ? 'bg-muted-foreground/30' : 'bg-primary',
        )}
      >
        <span className="rounded-full bg-background p-[2px]">
          <UserAvatar
            image={person.image}
            name={person.name}
            size={size}
            className="block rounded-full bg-muted object-cover"
          />
        </span>
      </span>
      <span
        className={cn(
          'w-full truncate text-center text-[11.5px] leading-tight',
          person.allSeen ? 'text-muted-foreground' : 'font-semibold text-foreground',
        )}
      >
        {label}
      </span>
    </button>
  )
}

/** The feed's row of story avatars; renders nothing when nobody has a story. */
export function StoryRail() {
  const { t } = useTranslation()
  const people = useQuery(api.stories.rail)
  const [open, setOpen] = useState<{ people: StoryPerson[]; index: number } | null>(null)
  if (!people?.length) return null
  return (
    <section aria-label={t('stories.title')} className="-mx-4 mb-5">
      <ul className="flex gap-1 overflow-x-auto px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {people.map((p, i) => (
          <li key={p.userId}>
            <StoryRing person={p} size={60} onOpen={() => setOpen({ people, index: i })} />
          </li>
        ))}
      </ul>
      {open && (
        <StoryViewer people={open.people} start={open.index} onClose={() => setOpen(null)} />
      )}
    </section>
  )
}

function HabitBadge({ item }: { item: StoryItem }) {
  const { t } = useTranslation()
  return (
    <div
      className="flex max-w-full items-center gap-2 self-start rounded-full bg-black/55 py-1.5 pr-3 pl-2 text-white backdrop-blur-md"
      style={{ '--habit': habitColor(item.habitCategory ?? undefined) } as CSSProperties}
    >
      <span
        aria-hidden
        className="size-3.5 shrink-0 rounded-full bg-[var(--habit)] ring-2 ring-white/70"
      />
      <span className="min-w-0 truncate text-[13px] font-bold">{item.habitTitle}</span>
      {item.streak > 0 && (
        <span
          className="shrink-0 text-[12.5px] font-semibold text-white/85"
          aria-label={t('stories.streak', { days: item.streak })}
        >
          🔥 {item.streak}
        </span>
      )}
    </div>
  )
}

/**
 * Full-screen stories: tap right/left for next/previous (crossing people), hold
 * to pause, Esc or ✕ to close. Photos run 5s; a video runs its length (≤15s).
 */
export function StoryViewer({
  people: initial,
  start,
  onClose,
}: {
  people: StoryPerson[]
  start: number
  onClose: () => void
}) {
  const { t, i18n } = useTranslation()
  const [people, setPeople] = useState(initial)
  const [pi, setPi] = useState(start)
  const [ii, setIi] = useState(() => {
    const idx = initial[start]?.items.findIndex((it) => !it.seen) ?? -1
    return idx < 0 ? 0 : idx
  })
  const [held, setHeld] = useState(false)
  const [menu, setMenu] = useState(false)
  const [progress, setProgress] = useState(0)
  const [muted, setMuted] = useState(false)
  const [soundBlocked, setSoundBlocked] = useState(false)
  const video = useRef<HTMLVideoElement>(null)
  const markSeen = useMutation(api.stories.markSeen)
  const remove = useMutation(api.stories.remove)
  const marked = useRef(new Set<string>())
  const press = useRef<{ at: number; x: number } | null>(null)
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const person = people[pi]
  const item = person?.items[ii]
  const paused = held || menu

  const go = useCallback(
    (dir: 1 | -1) => {
      setProgress(0)
      setMenu(false)
      if (!person) return
      const next = ii + dir
      if (next >= 0 && next < person.items.length) return setIi(next)
      const np = pi + dir
      if (np < 0) return setIi(0)
      if (np >= people.length) return onClose()
      setPi(np)
      setIi(dir === 1 ? 0 : people[np].items.length - 1)
    },
    [ii, pi, people, person, onClose],
  )

  useEffect(() => {
    if (!item || person.isMe || item.seen || marked.current.has(item.id)) return
    marked.current.add(item.id)
    void markSeen({ itemId: item.id as Id<'storyItems'> }).catch(() => {})
  }, [item, person, markSeen])

  useEffect(() => {
    if (!item || item.kind !== 'photo' || paused) return
    let last = performance.now()
    let raf = 0
    const tick = (now: number) => {
      const dt = now - last
      last = now
      setProgress((p) => {
        const n = p + dt / PHOTO_MS
        return n >= 1 ? 1 : n
      })
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [item, paused])

  useEffect(() => {
    if (progress >= 1) go(1)
  }, [progress, go])

  useEffect(() => {
    const v = video.current
    if (!v || item?.kind !== 'video') return
    if (paused) return void v.pause()
    v.play().catch(() => {
      // Autoplay with sound blocked: play muted and offer a tap to unmute.
      if (v.muted) return
      v.muted = true
      setMuted(true)
      setSoundBlocked(true)
      void v.play().catch(() => {})
    })
  }, [item, paused])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        if (menu) setMenu(false)
        else onClose()
      } else if (e.key === 'ArrowRight') go(1)
      else if (e.key === 'ArrowLeft') go(-1)
      else if (e.key === ' ') {
        e.preventDefault()
        setHeld((h) => !h)
      }
    }
    window.addEventListener('keydown', onKey)
    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = overflow
    }
  }, [go, menu, onClose])

  if (!person || !item) return null

  const onDown = (e: React.PointerEvent) => {
    press.current = { at: performance.now(), x: e.clientX }
    holdTimer.current = setTimeout(() => setHeld(true), HOLD_MS)
  }
  const onUp = (e: React.PointerEvent) => {
    if (holdTimer.current) clearTimeout(holdTimer.current)
    const p = press.current
    press.current = null
    setHeld(false)
    if (!p || performance.now() - p.at >= HOLD_MS) return
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    go(p.x - rect.left < rect.width / 3 ? -1 : 1)
  }

  const deleteItem = () => {
    const id = item.id
    setMenu(false)
    void remove({ itemId: id as Id<'storyItems'> })
      .then(() => toast.success(t('stories.deleted')))
      .catch(() => toast.error(t('stories.deleteFailed')))
    const items = person.items.filter((it) => it.id !== id)
    if (!items.length) {
      const rest = people.filter((_, i) => i !== pi)
      if (pi >= rest.length) return onClose()
      setPeople(rest)
      setIi(0)
    } else {
      setPeople(people.map((p, i) => (i === pi ? { ...p, items } : p)))
      setIi(Math.min(ii, items.length - 1))
    }
    setProgress(0)
  }

  const name = person.isMe ? t('stories.yourStory') : person.name
  const label = item.caption ?? item.habitTitle

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('stories.viewer', { name })}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black text-white select-none"
    >
      <div className="relative h-full w-full max-w-[min(100vw,calc(100dvh*9/16))] sm:h-[calc(100%-2rem)]">
        <div className="relative flex size-full flex-col overflow-hidden bg-neutral-900 sm:rounded-2xl">
          <div
            className="absolute inset-0 touch-none"
            onPointerDown={onDown}
            onPointerUp={onUp}
            onPointerCancel={() => {
              if (holdTimer.current) clearTimeout(holdTimer.current)
              setHeld(false)
            }}
            onContextMenu={(e) => e.preventDefault()}
          >
            {item.kind === 'video' && item.videoUrl ? (
              <video
                key={item.id}
                ref={video}
                src={item.videoUrl}
                poster={item.url ?? undefined}
                aria-label={label}
                playsInline
                muted={muted}
                onTimeUpdate={(e) => {
                  const v = e.currentTarget
                  const total = Math.min(v.duration * 1000 || VIDEO_MAX_MS, VIDEO_MAX_MS)
                  setProgress(Math.min(1, (v.currentTime * 1000) / total))
                }}
                onEnded={() => go(1)}
                className="size-full object-cover"
              />
            ) : (
              <img
                key={item.id}
                src={item.url ?? undefined}
                alt={label}
                draggable={false}
                className="size-full object-cover"
              />
            )}
          </div>

          <div className="pointer-events-none absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/60 to-transparent" />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/60 to-transparent" />

          <div className="absolute inset-x-0 top-0 flex flex-col gap-3 px-3 pt-[max(env(safe-area-inset-top),12px)]">
            <div className="flex gap-1" aria-hidden>
              {person.items.map((it, i) => (
                <span
                  key={it.id}
                  className="h-[3px] flex-1 overflow-hidden rounded-full bg-white/35"
                >
                  <span
                    className="block h-full rounded-full bg-white"
                    style={{ width: `${(i < ii ? 1 : i === ii ? progress : 0) * 100}%` }}
                  />
                </span>
              ))}
            </div>
            <div className="flex items-center gap-2.5">
              <UserAvatar
                image={person.image}
                name={person.name}
                size={32}
                className="size-8 rounded-full bg-white/20 object-cover"
              />
              <span className="min-w-0 truncate text-sm font-bold">{name}</span>
              <span className="shrink-0 text-[12.5px] text-white/70">
                {storyAgo(item.createdAt, i18n.language)}
              </span>
              <span className="ml-auto flex items-center gap-1">
                {person.isMe && (
                  <span className="relative">
                    <button
                      type="button"
                      aria-label={t('stories.more')}
                      aria-expanded={menu}
                      onClick={() => setMenu((m) => !m)}
                      className="grid size-9 cursor-pointer place-items-center rounded-full hover:bg-white/15"
                    >
                      <MoreHorizontal className="size-5" />
                    </button>
                    {menu && (
                      <div className="absolute top-10 right-0 z-10 w-48 overflow-hidden rounded-xl bg-neutral-800 py-1 shadow-xl ring-1 ring-white/10">
                        <button
                          type="button"
                          autoFocus
                          onClick={deleteItem}
                          className="flex w-full cursor-pointer items-center gap-2 px-3 py-2.5 text-left text-sm font-semibold text-red-400 hover:bg-white/10"
                        >
                          <Trash2 className="size-4" />
                          {t('stories.delete')}
                        </button>
                      </div>
                    )}
                  </span>
                )}
                <button
                  type="button"
                  aria-label={t('stories.close')}
                  onClick={onClose}
                  className="grid size-9 cursor-pointer place-items-center rounded-full hover:bg-white/15"
                >
                  <X className="size-5" />
                </button>
              </span>
            </div>
          </div>

          {item.kind === 'video' && (muted || soundBlocked) && (
            <button
              type="button"
              onClick={() => {
                const next = !muted
                setMuted(next)
                setSoundBlocked(false)
                if (video.current) video.current.muted = next
              }}
              className="absolute top-1/2 left-1/2 flex -translate-x-1/2 -translate-y-1/2 cursor-pointer items-center gap-2 rounded-full bg-black/55 px-4 py-2 text-sm font-semibold backdrop-blur-md"
            >
              {muted ? <VolumeX className="size-4" /> : <Volume2 className="size-4" />}
              {muted ? t('stories.unmute') : t('stories.mute')}
            </button>
          )}

          <div className="absolute inset-x-0 bottom-0 flex flex-col gap-2 px-4 pb-[max(env(safe-area-inset-bottom),20px)]">
            {item.caption && (
              <p className="text-[15px] leading-snug font-semibold drop-shadow">{item.caption}</p>
            )}
            <HabitBadge item={item} />
          </div>
        </div>

        <button
          type="button"
          aria-label={t('stories.previous')}
          onClick={() => go(-1)}
          className="absolute top-1/2 -left-14 hidden size-10 -translate-y-1/2 cursor-pointer place-items-center rounded-full bg-white/15 hover:bg-white/25 sm:grid"
        >
          <ChevronLeft className="size-5" />
        </button>
        <button
          type="button"
          aria-label={t('stories.next')}
          onClick={() => go(1)}
          className="absolute top-1/2 -right-14 hidden size-10 -translate-y-1/2 cursor-pointer place-items-center rounded-full bg-white/15 hover:bg-white/25 sm:grid"
        >
          <ChevronRight className="size-5" />
        </button>
      </div>
    </div>,
    document.body,
  )
}
